import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Icon, { IconName } from "../../../shared/components/Icon";
import { EXTERNAL, PATHS } from "../../../paths";

interface Slide {
  eyebrow: string;
  title: string;
  text: string;
  button: string;
  to: string;
  external?: boolean;
  image: string;
  alt: string;
  tagIcon: IconName;
  tag: string;
}

const SLIDES: Slide[] = [
  {
    eyebrow: "2011'den Beri",
    title: "44 Ülkedeki Global Gücü Yerel Uzmanlıkla Birleştiriyoruz",
    text: "Uluslararası standartlardaki uzman ekiplerimizle, sektörünüzün ihtiyaçlarına özel itfaiye hizmetleri sunuyoruz.",
    button: "Hakkımızda",
    to: PATHS.about,
    image: "/uploads/site/hero-itfaiye.jpg",
    alt: "Securitas itfaiyecisi yangına müdahale ediyor",
    tagIcon: "cert",
    tag: "NFPA, TÜYAK, FECF, NFSA ve JOIFF üyesi",
  },
  {
    eyebrow: "Multi Rol Hizmetleri",
    title: "Güvenlik ve İtfaiye Hizmetini Birleştiren Profesyonel Çözümler",
    text: "Güvenlik ve yangın güvenliği yetkinliklerini tek rolde birleştiren Multi Rol Güvenlik Görevlileri; rutin güvenlik hizmetlerinin yanı sıra yangın risklerinin tespiti ve ilk müdahale süreçlerinde görev alır.",
    button: "İtfaiye Eğitimi Almış Multi Rol Görevlileri",
    to: PATHS.multiRole,
    image: "/uploads/site/hero-multirol.jpg",
    alt: "Multi Rol Güvenlik Görevlisi yangın söndürücüyle müdahale ediyor",
    tagIcon: "pin",
    tag: "Saha, ilk müdahale eğitimi",
  },
  {
    eyebrow: "Risk Analizi",
    title: "Profesyonel Risk Analizi",
    text: "Kritik noktaları, tahliye planlarını ve önleyici sistemleri uzman risk analiz ekibimizle değerlendirerek işletmenize özel çözümler oluşturuyoruz.",
    button: "Yangın Risk Analizi ve Danışmanlığı",
    to: EXTERNAL.contact,
    external: true,
    image: "/uploads/site/hero-risk.jpg",
    alt: "Dumanlı koridorda ilerleyen itfaiyeci",
    tagIcon: "search",
    tag: "Tesise özel yangın risk değerlendirmesi",
  },
];

const AUTOPLAY_MS = 7000;

const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
};

const HeroSlider: FC = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [height, setHeight] = useState<number>();
  const timer = useRef<number>();
  const heroRef = useRef<HTMLElement>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inView = useRef(true);

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const go = useCallback(
    (next: number) => setIndex((next + SLIDES.length) % SLIDES.length),
    []
  );

  useEffect(() => {
    if (paused || reduceMotion) return;
    timer.current = window.setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), AUTOPLAY_MS);
    return () => window.clearInterval(timer.current);
  }, [paused, reduceMotion, index]);

  // Üç slayt aynı hücrede üst üste durur; yükseklik en uzun metne göre kalmasın diye aktif slayta göre ayarlanır.
  useLayoutEffect(() => {
    const active = contentRefs.current[index];
    if (!active) return;
    const measure = () => setHeight(active.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(active);
    return () => observer.disconnect();
  }, [index]);

  // Sol/sağ ok tuşları, hero ekranda görünürken çalışır
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      inView.current = entry.isIntersecting;
    });
    observer.observe(hero);

    const onKeyDown = (event: KeyboardEvent) => {
      if (!inView.current || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === "ArrowLeft") go(index - 1);
      else if (event.key === "ArrowRight") go(index + 1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [go, index]);

  return (
    <section
      ref={heroRef}
      className="it-hero"
      aria-roledescription="carousel"
      aria-label="Öne çıkanlar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="it-wrap it-slides"
        style={height ? { height } : undefined}
        aria-live={paused ? "polite" : "off"}
      >
        {SLIDES.map((slide, i) => (
          <div
            key={slide.title}
            className={`it-slide${i === index ? " on" : ""}`}
            role="group"
            aria-label={`${i + 1} / ${SLIDES.length}`}
            aria-hidden={i !== index}
          >
            <div className="it-slide-in" ref={(el) => (contentRefs.current[i] = el)}>
              <div>
                <div className="it-eyebrow">{slide.eyebrow}</div>
                {i === 0 ? <h1>{slide.title}</h1> : <h2 className="it-slide-title">{slide.title}</h2>}
                <p>{slide.text}</p>
                {slide.external ? (
                  <a
                    className="it-btn"
                    href={slide.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={i === index ? 0 : -1}
                  >
                    {slide.button}
                  </a>
                ) : (
                  <Link className="it-btn" to={slide.to} tabIndex={i === index ? 0 : -1}>
                    {slide.button}
                  </Link>
                )}
              </div>
              <div className="it-hero-photo">
                <img src={slide.image} alt={slide.alt} />
                <div className="it-hp-tag">
                  <Icon name={slide.tagIcon} /> {slide.tag}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="it-dots">
        <button
          type="button"
          className="it-arrow"
          aria-label="Önceki slayt"
          onClick={() => go(index - 1)}
        >
          <Icon name="arrow" className="it-arrow-prev" />
        </button>
        {SLIDES.map((slide, i) => (
          <button
            type="button"
            key={slide.title}
            className={i === index ? "on" : undefined}
            aria-label={`${i + 1}. slayt`}
            onClick={() => go(i)}
          />
        ))}
        <button
          type="button"
          className="it-arrow"
          aria-label="Sonraki slayt"
          onClick={() => go(index + 1)}
        >
          <Icon name="arrow" />
        </button>
      </div>
    </section>
  );
};

export default HeroSlider;
